import "./DrawerAppBar.css";
import React, { useEffect } from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";

import {ReactComponent as MenuIcon} from "../icons/Menu24.svg";
import {ReactComponent as LogoutIcon} from "../icons/Logout24.svg";

import Container from "@mui/material/Container";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";

import { useNavigate, useLocation } from "react-router-dom";
import { logoutUser, getUserPriority } from "../services/UserService";

import { useJwtStorage, useLocalStorage } from "../hooks/useLocalStorage";
import { googleLogout } from "@react-oauth/google";

import { useIdleTimer } from "react-idle-timer";
import {
  getStudentMenuItems,
  getFacultyMenuItems,
  getVolunteerMenuItems,
} from "../services/MenuService";

const DrawerAppBar = ({
  showHamburger = true,
  showLogout = true,
  disableLogout = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [_productName] = useLocalStorage("Auth", "productName", "AllTimely");
  const [_school] = useLocalStorage("Auth", "school", null);
  const isNativeCapacitor =
    typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();

  const doLogout = () => {
    logoutUser();
    googleLogout();

    if (_school.checkinEnabled === "Y") {
      navigate("/checkin");
    } else {
      navigate("/");
    }
  };

  const isQrPage = () => {
    // return true if the page shows the QR (to avoid implementing a timeout)
    const qrPages = [
      "/teacher/pass/leave",
      "/teacher/score/tardy",
      "/teacher/pass/return",
      "/teacher/ad/signup",
      "/teacher/ad/attend",
      "/faculty/event/signup",
      "/admin/score/locationQr",
      "/teacher/student-list",
      "/guardian/event/signup/qr",
      "/op/verify"
    ];

    for (const page of qrPages) {
      if (location.pathname.startsWith(page)) {
        return true;
      }
    }

    return false;
  };

  const onIdle = () => {
    //isUserReauthenticated;
    // Close Modal Prompt
    // Do some idle action like log out your user
    if (!isQrPage() && !disableLogout) {
      doLogout();
    }
  };
  const [_jwt] = useJwtStorage();
  const [_user] = useLocalStorage("Auth", "user", null);

  const [_anchorElNav, setAnchorElNav] = React.useState(null);
  const [_anchorElUser, setAnchorElUser] = React.useState(null);
  const [_pages, setPages] = React.useState([]);

  const [_productSource] = useLocalStorage("Auth", "productSource", "");

  const initStudentMenu = () => {
    setPages(getStudentMenuItems(_school));
  };

  let timeout = 900000;
  if (_school != null) {
    // use school settings
    timeout = _school.loginTimeout * 60 * 1000;
  }
  // TIMEOUT IN MS. Native Capacitor apps should not auto-logout due to inactivity.
  useIdleTimer({
    onIdle,
    timeout: timeout,
    disabled: isNativeCapacitor,
  });

  useEffect(() => {
    const priority = getUserPriority(_jwt);

    if (priority === 500) {
      initStudentMenu();
    } else if (priority >= 600) {
      initTeacherMenu();
    } else if (priority === 525) {
      initVolunteerMenu();
    }
  }, []);

  const initVolunteerMenu = () => {
    setPages(getVolunteerMenuItems(_school, _jwt, _user));
  };

  const initTeacherMenu = () => {
    setPages(getFacultyMenuItems(_school, _jwt, _user));
  };

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = (page) => {
    setAnchorElNav(null);

    if (page == null) return;

    if (!page.isHeader) {
      navigate(page.path);
    }
  };

  const handleCloseUserMenu = (e, itemClicked) => {
    setAnchorElUser(null);
    if (itemClicked === "Logout") {
      if (_productSource === "self") {
        doLogout();
      } else {
        const redirectUrl = `https://${_school.subdomain}.alltimely.com/auth/logout`;
        logoutUser();
        window.location = redirectUrl;
      }
    } else if (itemClicked === "Settings") {
      navigate("/teacher/settings");
    }
  };

  return (
    <>
      <Box sx={{ flexGrow: 1 }} className="noprint">
        <AppBar position="static" sx={{ backgroundColor: "#28292e" }}>
          <Container maxWidth="xl">
            <Toolbar disableGutters>
              {showHamburger && (
                <Box sx={{ flexGrow: 1 }}>
                  <IconButton
                    size="large"
                    aria-label="account of current user"
                    aria-controls="menu-appbar"
                    aria-haspopup="true"
                    onClick={handleOpenNavMenu}
                    color="inherit"
                  >
                    <MenuIcon />
                  </IconButton>
                  <Menu
                    id="menu-appbar"
                    anchorEl={_anchorElNav}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    open={Boolean(_anchorElNav)}
                    onClose={handleCloseNavMenu}
                    sx={{
                      display: "block",
                    }}
                  >
                    {_pages.map((page) => (
                      <MenuItem
                        key={page.text}
                        sx={{ minHeight: 32, pt: 0.5, pb: 0 }}
                        onClick={() => handleCloseNavMenu(page)}
                      >
                        {page.isHeader && (
                          <span
                            style={{
                              fontSize: "18px",
                              fontWeight: 600,
                              ml: -0.7,
                            }}
                          >
                            {page.text}
                          </span>
                        )}
                        {!page.isHeader && <span>{page.text}</span>}
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              )}

              <Typography
                variant="h5"
                noWrap
                component="a"
                sx={{
                  mr: 2,
                  flexGrow: 1,
                  fontFamily: "Arial",
                  fontWeight: 700,
                  fontSize: "1em",
                  color: "inherit",
                  textDecoration: "none",
                }}
              >
                {_productName}
              </Typography>

              {showLogout && (
                <Box sx={{ flexGrow: 0 }}>
                  <Tooltip title="Settings Menu">
                    <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                      <LogoutIcon sx={{ color: "white" }} />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    sx={{ mt: "45px" }}
                    id="menu-appbar"
                    anchorEl={_anchorElUser}
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    open={Boolean(_anchorElUser)}
                    onClose={handleCloseUserMenu}
                  >
                    {getUserPriority(_jwt) > 525 && (
                      <MenuItem
                        key={"Settings"}
                        onClick={(e) => handleCloseUserMenu(e, "Settings")}
                      >
                        User Settings
                      </MenuItem>
                    )}
                    <MenuItem
                      key={"Logout"}
                      onClick={(e) => handleCloseUserMenu(e, "Logout")}
                    >
                      Logout
                    </MenuItem>
                  </Menu>
                </Box>
              )}
            </Toolbar>
          </Container>
        </AppBar>
      </Box>
    </>
  );
};

export default DrawerAppBar;
